import React from 'react'
import { ChevronRight } from 'lucide-react'
import { SvgIcon } from '../svg-icon'
import { CodeWidgetAnalysis } from '../widgets/CodeWidget/analysis/code-widget-analysis'

type TreeNode = { label: string; children: TreeNode[]; widgets: number[] }

/** Where each unique page sits, and where the same one sits more than once. */
export function AppTree(props: { widgets: CodeWidgetAnalysis[]; onShowWidget: (index: number) => void }) {
  const tree = React.useMemo(
    () =>
      buildPathTree(props.widgets.flatMap((widget, index) => widget.paths.map((path) => ({ path, marker: index })))),
    [props.widgets]
  )

  return (
    <div>
      {tree.children.map((child) => (
        <TreeBranch
          key={child.label}
          node={child}
          marker={(index) => (
            <button
              key={index}
              type="button"
              title="Go to this widget"
              onClick={(event) => {
                event.preventDefault()
                props.onShowWidget(index)
              }}
              className="rounded-sm border border-primary/30 bg-primary/10 px-1.5 text-xs text-primary transition-colors hover:border-primary/60 hover:bg-primary/20"
            >
              Widget {index + 1}
            </button>
          )}
        />
      ))}
    </div>
  )
}

/**
 * The same tree, for one widget: where this page sits, once per placement.
 *
 * Leaves are numbered rather than named, and the numbers are the ones the config-field values use — so "2."
 * there and "2." here are the same placement. Where the branches split is where the placements stop being
 * alike, which is the question a list of near-identical paths made you answer by eye.
 */
export function WidgetTree(props: { paths: string[] }) {
  const tree = React.useMemo(
    () => buildPathTree(props.paths.map((path, index) => ({ path, marker: index }))),
    [props.paths]
  )

  return (
    <div className="mt-1 rounded-md bg-muted px-2 py-1">
      {tree.children.map((child) => (
        <TreeBranch
          key={child.label}
          node={child}
          marker={(index) => (
            <span
              key={index}
              className="rounded-sm border border-border bg-card px-1.5 font-mono text-xs text-muted-foreground"
            >
              {index + 1}
            </span>
          )}
        />
      ))}
    </div>
  )
}

function TreeBranch(props: { node: TreeNode; marker: (index: number) => React.ReactNode }) {
  const { node } = props
  const [position, type] = splitLabel(node.label)

  const row = (
    <span className="inline-flex flex-wrap items-center gap-2">
      {position && <span className="font-mono text-xs text-muted-foreground">{position}</span>}
      <span className="font-mono text-xs">{type}</span>
      {node.widgets.map((index) => props.marker(index))}
    </span>
  )

  if (node.children.length === 0) {
    return <div className="border-l border-border py-0.5 pl-4">{row}</div>
  }

  return (
    <details className="group/branch border-l border-border pl-1" open>
      <summary className="flex cursor-pointer list-none items-center gap-1 py-0.5 hover:bg-muted/50">
        <SvgIcon
          icon={ChevronRight}
          size={12}
          className="shrink-0 text-muted-foreground transition-transform group-open/branch:rotate-90"
        />
        {row}
      </summary>
      <div className="pl-3">
        {node.children.map((child) => (
          <TreeBranch key={child.label} node={child} marker={props.marker} />
        ))}
      </div>
    </details>
  )
}

/**
 * The app's layout, as far as it leads to Code Widgets.
 *
 * Built from the path strings rather than from a second walk of the config: the paths are already a
 * hierarchy, and deriving the tree from them means the two cannot disagree about where something sits.
 * Only branches that end in a widget appear — this is a map to the things being reported.
 */
function buildPathTree(entries: { path: string; marker: number }[]): TreeNode {
  const root: TreeNode = { label: '', children: [], widgets: [] }

  for (const entry of entries) {
    let node = root
    for (const label of treeLabels(entry.path)) {
      const existing = node.children.find((child) => child.label === label)
      if (existing) {
        node = existing
      } else {
        const child: TreeNode = { label, children: [], widgets: [] }
        node.children.push(child)
        node = child
      }
    }
    node.widgets.push(entry.marker)
  }

  return root
}

/**
 * Rewrite a path for the tree: each row names a widget, prefixed by the position it occupies in its parent.
 *
 * In a path the bracket sits on the container — `SplitLayout[0] > BBSideMenu` — because a path is read as
 * directions. A tree is read as places, so the bracket moves to the child it points at: `SplitLayout` with
 * `[0]: BBSideMenu` under it.
 */
function treeLabels(path: string): string[] {
  const parsed = path.split(' > ').map(parseSegment)
  return parsed.map((segment, index) => {
    const position = index === 0 ? '' : parsed[index - 1].position
    return position ? `${position}: ${segment.type}` : segment.type
  })
}

/**
 * Split `BBSideMenu[1 "Taxonomie"]` into its type and its position. Anchored at the end so an embedded app
 * keeps its name intact: `component "OTL Taxonomie [COMPONENT]"` ends in a quote, not a bracket.
 */
function parseSegment(segment: string): { type: string; position: string } {
  const match = segment.match(/^(.*?)(\[[^\]]*\])$/)
  return match ? { type: match[1], position: match[2] } : { type: segment, position: '' }
}

/** "[1 \"Taxonomie\"]: BBSideMenu" back into its two parts, so the position can be dimmed against the type. */
function splitLabel(label: string): [string, string] {
  const match = label.match(/^(\[[^\]]*\]):\s*(.*)$/)
  return match ? [match[1], match[2]] : ['', label]
}
